import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, Search, Calendar, Clock, Target, TrendingUp,
  MoreVertical, Play, Pause, Archive, Trash2, Edit2,
  FolderKanban, AlertTriangle, CheckCircle, Filter
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays } from 'date-fns';
import { useProject } from '@/contexts/ProjectContext';

const STATUS_CONFIG = {
  planning: { color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', label: 'Planning' },
  active: { color: 'bg-[#1E4E8C]/20 text-[#1E4E8C] border-[#1E4E8C]/30', label: 'Active' },
  on_hold: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'On Hold' },
  completed: { color: 'bg-[#0E7C6B]/20 text-[#0E7C6B] border-[#0E7C6B]/30', label: 'Completed' },
  cancelled: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Cancelled' }
};

export default function SchedulingProjectsList() {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    budget: 0
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadProjects();
  }, [selectedProject]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const params = selectedProject?.project_id
        ? `?portfolio_project_id=${selectedProject.project_id}`
        : '';
      const res = await api.get(`/scheduling/projects${params}`);
      setProjects(res.data || []);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    try {
      const payload = {
        ...newProject,
        portfolio_project_id: selectedProject?.project_id || null,
      };
      const res = await api.post('/scheduling/projects', payload);
      toast.success('Project created');
      setShowCreateModal(false);
      setNewProject({ name: '', description: '', start_date: new Date().toISOString().split('T')[0], budget: 0 });
      await loadProjects();
      navigate(`/scheduling/${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.error(detail || 'A macro schedule already exists for this project');
      } else {
        toast.error('Failed to create project');
      }
    }
  };

  const handleDeleteProject = async (projectId) => {
    setDeleting(true);
    try {
      await api.delete(`/scheduling/projects/${projectId}`);
      toast.success('Schedule project deleted');
      setDeleteConfirm(null);
      await loadProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = !search || 
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getHealthColor = (progress, spi) => {
    if (progress >= 100) return 'green';
    if (spi >= 0.9) return 'green';
    if (spi >= 0.8) return 'amber';
    return 'red';
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Project Scheduling</h1>
          <p className="text-sm text-slate-400 mt-1">Create and manage project schedules with CPM, EVM, and resource leveling</p>
        </div>
        <Button 
          data-testid="create-schedule-project-btn"
          onClick={() => setShowCreateModal(true)}
          className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
          disabled={projects.length > 0 && !!selectedProject}
          title={projects.length > 0 && selectedProject ? 'Only one macro schedule per project is allowed' : 'Create a new schedule'}
        >
          <Plus className="w-4 h-4 mr-2" />
          {projects.length > 0 && selectedProject ? 'Schedule Exists' : 'New Project'}
        </Button>
      </div>

      {/* Filters */}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            data-testid="scheduling-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search projects..."
            className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white"
          />
        </div>
        <select
          data-testid="scheduling-status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-[#1F2937] border border-[#3A3F4A] rounded-md text-white text-sm"
        >
          <option value="">All Status</option>
          <option value="planning">Planning</option>
          <option value="active">Active</option>
          <option value="on_hold">On Hold</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#1E4E8C] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-500">Loading projects...</span>
          </div>
        </div>
      ) : filteredProjects.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project, index) => {
            const metrics = project.metrics || {};
            const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.planning;
            const healthColor = getHealthColor(metrics.overall_progress, metrics.spi);
            const daysRemaining = project.finish_date 
              ? differenceInDays(parseISO(project.finish_date), new Date())
              : null;

            return (
              <motion.div
                key={project.id}
                data-testid={`schedule-project-${project.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/scheduling/${project.id}`)}
                className="glass-card p-5 cursor-pointer hover:border-[#1E4E8C]/50 transition-all group"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate group-hover:text-[#1E4E8C] transition-colors">
                      {project.name}
                    </h3>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{project.description || 'No description'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-[10px] font-medium rounded-full border ${status.color}`}>
                      {status.label}
                    </span>
                    <button
                      data-testid={`delete-schedule-${project.id}`}
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(project); }}
                      className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/20"
                      title="Delete schedule"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-400">Progress</span>
                    <span className="text-white font-medium">{metrics.overall_progress?.toFixed(1) || 0}%</span>
                  </div>
                  <div className="h-2 bg-[#3A3F4A] rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        healthColor === 'green' ? 'bg-[#0E7C6B]' :
                        healthColor === 'amber' ? 'bg-amber-400' : 'bg-red-400'
                      }`}
                      style={{ width: `${metrics.overall_progress || 0}%` }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 pt-3 border-t border-[#3A3F4A]">
                  <div className="text-center">
                    <p className="text-[10px] text-slate-500">Tasks</p>
                    <p className="text-sm font-semibold text-white">{metrics.total_tasks || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-slate-500">Critical</p>
                    <p className="text-sm font-semibold text-red-400">{metrics.critical_tasks || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-slate-500">SPI</p>
                    <p className={`text-sm font-semibold ${
                      (project.status === 'planning' && (metrics.overall_progress || 0) === 0)
                        ? 'text-slate-500'
                        : (metrics.spi || 0) >= 1 ? 'text-[#0E7C6B]' : 'text-red-400'
                    }`}>
                      {(project.status === 'planning' && (metrics.overall_progress || 0) === 0)
                        ? '—'
                        : (metrics.spi != null ? metrics.spi.toFixed(2) : '—')}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-slate-500">CPI</p>
                    <p className={`text-sm font-semibold ${
                      (project.status === 'planning' && (metrics.overall_progress || 0) === 0)
                        ? 'text-slate-500'
                        : (metrics.cpi || 0) >= 1 ? 'text-[#0E7C6B]' : 'text-red-400'
                    }`}>
                      {(project.status === 'planning' && (metrics.overall_progress || 0) === 0)
                        ? '—'
                        : (metrics.cpi != null ? metrics.cpi.toFixed(2) : '—')}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#3A3F4A]">
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <Calendar className="w-3 h-3" />
                    {project.start_date ? format(parseISO(project.start_date), 'MMM d, yyyy') : 'No date'}
                  </div>
                  {daysRemaining !== null && (
                    <span className={`text-xs ${daysRemaining < 0 ? 'text-red-400' : daysRemaining < 7 ? 'text-amber-400' : 'text-slate-400'}`}>
                      {daysRemaining < 0 ? `${Math.abs(daysRemaining)}d overdue` : `${daysRemaining}d remaining`}
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <FolderKanban className="w-16 h-16 text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No schedule projects</h3>
          <p className="text-sm text-slate-400 mb-4">
            {search || statusFilter ? 'Try adjusting your filters' : 'Create your first scheduling project to get started'}
          </p>
          {!search && !statusFilter && (
            <Button onClick={() => setShowCreateModal(true)} className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90">
              <Plus className="w-4 h-4 mr-2" /> Create Project
            </Button>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowCreateModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={e => e.stopPropagation()}
            className="bg-[#0f1629] border border-[#3A3F4A] rounded-xl w-full max-w-md p-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4">Create Schedule Project</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Project Name *</Label>
                <Input
                  data-testid="new-schedule-project-name"
                  value={newProject.name}
                  onChange={e => setNewProject(p => ({ ...p, name: e.target.value }))}
                  placeholder="Enter project name"
                  className="mt-1 bg-[#1F2937] border-[#3A3F4A] text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Description</Label>
                <Input
                  value={newProject.description}
                  onChange={e => setNewProject(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description"
                  className="mt-1 bg-[#1F2937] border-[#3A3F4A] text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Start Date</Label>
                <Input
                  type="date"
                  value={newProject.start_date}
                  onChange={e => setNewProject(p => ({ ...p, start_date: e.target.value }))}
                  className="mt-1 bg-[#1F2937] border-[#3A3F4A] text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Budget</Label>
                <Input
                  type="number"
                  value={newProject.budget}
                  onChange={e => setNewProject(p => ({ ...p, budget: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                  className="mt-1 bg-[#1F2937] border-[#3A3F4A] text-white"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button 
                data-testid="create-schedule-project-submit"
                className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90" 
                onClick={handleCreateProject}
              >
                Create Project
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={e => e.stopPropagation()}
            className="bg-[#0f1629] border border-red-500/30 rounded-xl w-full max-w-sm p-6"
            data-testid="delete-confirm-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-500/20">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-white">Delete Schedule</h2>
            </div>
            <p className="text-sm text-slate-400 mb-2">
              Are you sure you want to delete <span className="text-white font-medium">"{deleteConfirm.name}"</span>?
            </p>
            <p className="text-xs text-red-400/80 mb-6">
              This will permanently remove the schedule, all tasks, assignments, resources, and related roll-up data.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
              <Button
                data-testid="confirm-delete-btn"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleDeleteProject(deleteConfirm.id)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Schedule'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </DashboardLayout>
  );
}
